/**
 * Bijlagen bij de crew-mail (Joris, 21 okt 2026).
 *
 * De documenten van het project (projectflow → Documenten) met per stuk een vinkje
 * "meesturen met callsheet". De keuze staat per callsheet op de server. Bij "Crew informeren"
 * met e-mail gaan de aangevinkte documenten als échte bijlage mee; te groot of verdwenen meldt
 * de server vóór het versturen (dan gaat er niets). Downloaden kan alleen voor aangevinkte
 * documenten, achter de gewone login.
 *
 * Script (3 nov 2026): daarnaast kiest de PL zelf één script uit de scripttool (SharePoint). Dat gaat
 * bij versturen als PDF mee; standaard gaat er geen script mee.
 *
 * Bewust één bestand, gelijk op develop en main: de CallSheetManager verschilt per branch,
 * deze bouwstenen niet.
 */
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Paper, Radio, Tooltip, Typography,
} from '@mui/material';
import { AttachFile as BijlageIcon, DescriptionOutlined as ScriptIcon } from '@mui/icons-material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { voerUitgesteldUit } from '../../utils/uitgesteld';

const GRIJS = '#64748b';

/** 65991 → "64 kB", 1572864 → "1,5 MB" — zelfde notatie als de server. */
export function grootteTekst(n) {
  const b = Number(n) || 0;
  if (b < 1024 * 1024) return `${Math.max(1, Math.round(b / 1024))} kB`;
  return `${(b / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

export const gekozenDocumenten = (ov) => (ov?.documenten || []).filter((d) => d.gekozen);

/** Documenten plus het script (dat als PDF meegaat). */
export const aantalBijlagen = (ov) => gekozenDocumenten(ov).length + (ov?.script ? 1 : 0);

const datumKort = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '';
};

/** Venster: één script uit de scripttool kiezen. */
function ScriptKiezer({ callsheetId, open, huidig, onSluit, onGekozen }) {
  const [opties, setOpties] = useState(null);
  const [fout, setFout] = useState(null);
  const [keuze, setKeuze] = useState(huidig || null);
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let levend = true;
    setOpties(null); setFout(null); setKeuze(huidig || null);
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${callsheetId}/bijlagen/scripts`);
        const d = await leesJson(r, 'Scripts laden');
        if (levend) setOpties(d);
      } catch (e) { if (levend) setFout(e.message); }
    })();
    return () => { levend = false; };
  }, [open, callsheetId, huidig]);

  const bewaar = async () => {
    if (!keuze || bezig) return;
    setBezig(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${callsheetId}/bijlagen/script`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ item: keuze }),
      });
      const d = await leesJson(r, 'Script kiezen');
      onGekozen(d.script);
      toast.success(`${d.script?.pdfNaam || 'Script'} gaat mee met de crew-mail`, { duration: 2500 });
    } catch (e) { meldFout(e, 'Script kiezen'); }
    finally { setBezig(false); }
  };

  const reeksen = opties?.reeksen || [];
  return (
    <Dialog open={open} onClose={bezig ? undefined : onSluit} maxWidth="sm" fullWidth>
      <DialogTitle>Script meesturen</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: GRIJS, mb: 1.5 }}>
          Het gekozen script gaat als PDF mee met de crew-mail. Wil je een nieuwere versie sturen, kies die dan hier opnieuw.
        </Typography>
        {!opties && !fout && <CircularProgress size={18} />}
        {fout && <Alert severity="error">{fout}</Alert>}
        {(opties?.meldingen || []).map((m) => <Alert key={m} severity="info" sx={{ mb: 1 }}>{m}</Alert>)}
        {opties && !reeksen.length && (
          <Typography variant="body2" sx={{ color: GRIJS }}>Geen scripts gevonden bij dit project. Koppel de map in het Script-paneel op de projectpagina.</Typography>
        )}
        {reeksen.map((r) => (
          <Box key={r.sleutel} sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: GRIJS, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              {r.titel}{r.aangeleverd ? ' · aangeleverd' : ''}
            </Typography>
            {r.versies.map((v) => (
              <Box key={v.item} component="label" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
                <Radio size="small" checked={keuze === v.item} onChange={() => setKeuze(v.item)}
                  inputProps={{ 'aria-label': v.naam }} />
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{v.naam}</Typography>
                {v.def && <Chip label="DEF" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />}
                <Typography variant="caption" sx={{ color: GRIJS, whiteSpace: 'nowrap' }}>
                  {[v.versie, datumKort(v.gewijzigdOp)].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onSluit} disabled={bezig}>Annuleren</Button>
        <Button variant="contained" onClick={bewaar} disabled={!keuze || bezig}>Meesturen</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Waarom de mail niet kan met deze bijlagen (of null). Alleen relevant als e-mail aan staat. */
export function bijlagenProbleem(ov) {
  if (!ov) return null;
  if (ov.bezig) return 'Bijlage wordt opgeslagen…';
  if ((ov.ontbrekend || []).length) {
    return 'Een aangevinkte bijlage staat niet meer bij het project. Kies de bijlagen opnieuw.';
  }
  if (ov.teGroot) {
    return `Samen ${grootteTekst(ov.totaalBytes)}: te groot als bijlage, maximaal ${ov.maxTekst || grootteTekst(ov.maxBytes)}. Haal een vinkje weg.`;
  }
  return null;
}

async function openBijlage(callsheetId, doc) {
  try {
    const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${callsheetId}/bijlagen/${doc.id}/download`);
    const d = await leesJson(r, 'Bijlage openen');
    window.open(d.url, '_blank', 'noopener');
  } catch (e) { meldFout(e, 'Bijlage openen'); }
}

/** Het blok op de callsheet: documenten van het project met vinkje. */
export default function CallsheetBijlagen({ callsheetId, onOverzicht, paperSx }) {
  const [ov, setOv] = useState(null);
  const [fout, setFout] = useState(null);
  const [bezigId, setBezigId] = useState(null);
  const [kiezen, setKiezen] = useState(false);
  const [scriptBezig, setScriptBezig] = useState(false);

  const zet = useCallback((nieuw) => { setOv(nieuw); onOverzicht?.(nieuw); }, [onOverzicht]);

  const laad = useCallback(async () => {
    if (!callsheetId) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${callsheetId}/bijlagen`);
      zet(await leesJson(r, 'Bijlagen laden'));
      setFout(null);
    } catch (e) { setFout(e.message); }
  }, [callsheetId, zet]);
  useEffect(() => { laad(); }, [laad]);

  const wissel = async (doc) => {
    if (!ov || bezigId) return;
    const ids = doc.gekozen ? ov.gekozen.filter((i) => i !== doc.id) : [...ov.gekozen, doc.id];
    setBezigId(doc.id);
    onOverzicht?.({ ...ov, bezig: true });   // venster: niet versturen zolang de keuze wordt opgeslagen
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${callsheetId}/bijlagen`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids }),
      });
      zet(await leesJson(r, 'Bijlage opslaan'));
      toast.success(doc.gekozen ? `${doc.bestandsnaam} gaat niet meer mee` : `${doc.bestandsnaam} gaat mee met de crew-mail`, { duration: 2500 });
    } catch (e) { meldFout(e, 'Bijlage opslaan'); onOverzicht?.(ov); }
    finally { setBezigId(null); }
  };

  const zetScript = (script) => { if (ov) zet({ ...ov, script }); setKiezen(false); };
  const haalScriptWeg = async () => {
    if (!ov?.script || scriptBezig) return;
    setScriptBezig(true);
    onOverzicht?.({ ...ov, bezig: true });
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${callsheetId}/bijlagen/script`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ item: null }),
      });
      zetScript((await leesJson(r, 'Script weghalen')).script);
      toast.success('Het script gaat niet meer mee', { duration: 2500 });
    } catch (e) { meldFout(e, 'Script weghalen'); onOverzicht?.(ov); }
    finally { setScriptBezig(false); }
  };

  const docs = ov?.documenten || [];
  const probleem = bijlagenProbleem(ov);
  return (
    <Paper sx={{ p: 2.5, mb: 3, border: '1px solid #e5e7eb', ...(paperSx || {}) }} data-testid="callsheet-bijlagen">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: docs.length ? 1 : 0, flexWrap: 'wrap' }}>
        <BijlageIcon sx={{ fontSize: 20, color: GRIJS }} />
        <Typography sx={{ fontWeight: 700 }}>Bijlagen bij de crew-mail</Typography>
        {ov && gekozenDocumenten(ov).length > 0 && (
          <Typography variant="body2" sx={{ color: GRIJS }}>
            · {gekozenDocumenten(ov).length} gekozen, samen {grootteTekst(ov.totaalBytes)} (max {ov.maxTekst})
          </Typography>
        )}
        {!ov && !fout && <CircularProgress size={14} sx={{ ml: 1 }} />}
      </Box>
      {fout && <Alert severity="error" sx={{ mt: 1 }}>{fout}</Alert>}
      {ov && !ov.projectId && (
        <Typography variant="body2" sx={{ color: GRIJS }}>Deze callsheet hangt niet aan een project, dus er zijn geen projectdocumenten om mee te sturen.</Typography>
      )}
      {ov && ov.projectId && !docs.length && (
        <Typography variant="body2" sx={{ color: GRIJS }}>Nog geen documenten bij dit project. Upload ze in de projectflow (blok Documenten), dan kun je ze hier aanvinken.</Typography>
      )}
      {docs.map((d) => (
        <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={d.gekozen ? 'Gaat mee als bijlage bij "Crew informeren" per e-mail' : 'Meesturen met callsheet'}>
            <span>
              <Checkbox size="small" checked={Boolean(d.gekozen)} disabled={bezigId !== null}
                onChange={() => wissel(d)} inputProps={{ 'aria-label': `${d.bestandsnaam} meesturen met callsheet` }} />
            </span>
          </Tooltip>
          {d.gekozen ? (
            <Box component="button" type="button" onClick={() => openBijlage(callsheetId, d)}
              sx={{ border: 0, background: 'none', p: 0, cursor: 'pointer', font: 'inherit', fontSize: '0.875rem', color: '#1d4ed8', textAlign: 'left', '&:hover': { textDecoration: 'underline' } }}>
              {d.bestandsnaam}
            </Box>
          ) : (
            <Typography variant="body2">{d.bestandsnaam}</Typography>
          )}
          <Typography variant="caption" sx={{ color: GRIJS, ml: 0.5, whiteSpace: 'nowrap' }}>{grootteTekst(d.grootte)}</Typography>
          {bezigId === d.id && <CircularProgress size={12} sx={{ ml: 1 }} />}
        </Box>
      ))}
      {ov && ov.projectId && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, pt: 1.5, borderTop: '1px solid #f1f5f9', flexWrap: 'wrap' }}
          data-testid="callsheet-script">
          <ScriptIcon sx={{ fontSize: 18, color: GRIJS }} />
          {ov.script ? (
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
              Script: {ov.script.pdfNaam} <Box component="span" sx={{ color: GRIJS }}>(als PDF)</Box>
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ flex: 1, color: GRIJS }}>Geen script. Kies er een om het als PDF mee te sturen.</Typography>
          )}
          <Button size="small" onClick={() => setKiezen(true)} disabled={scriptBezig}>
            {ov.script ? 'Ander script' : 'Script kiezen'}
          </Button>
          {ov.script && (
            <Button size="small" color="inherit" onClick={haalScriptWeg} disabled={scriptBezig}>Niet meesturen</Button>
          )}
        </Box>
      )}
      {probleem && <Alert severity="warning" sx={{ mt: 1 }}>{probleem}</Alert>}
      <ScriptKiezer callsheetId={callsheetId} open={kiezen} huidig={ov?.script?.item}
        onSluit={() => setKiezen(false)} onGekozen={zetScript} />
    </Paper>
  );
}

/** In het venster "Crew informeren": wat er met de e-mail meegaat. */
export function BijlagenInDialoog({ overzicht, mail }) {
  const gekozen = gekozenDocumenten(overzicht);
  const script = overzicht?.script;
  if (!overzicht || (!gekozen.length && !script)) {
    return mail ? (
      <Typography variant="body2" sx={{ color: GRIJS, mt: 1 }} data-testid="bijlagen-dialoog">
        Geen bijlagen. Vink ze aan in het blok "Bijlagen bij de crew-mail".
      </Typography>
    ) : null;
  }
  const probleem = bijlagenProbleem(overzicht);
  return (
    <Box sx={{ mt: 1.5 }} data-testid="bijlagen-dialoog">
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        📎 {mail ? 'Mee met de e-mail' : 'Bijlagen (gaan alleen mee met de e-mail)'}:
      </Typography>
      {gekozen.map((d) => (
        <Typography key={d.id} variant="body2" sx={{ color: mail ? 'text.primary' : GRIJS, pl: 2 }}>
          {d.bestandsnaam} <Box component="span" sx={{ color: GRIJS }}>({grootteTekst(d.grootte)})</Box>
        </Typography>
      ))}
      {script && (
        <Typography variant="body2" sx={{ color: mail ? 'text.primary' : GRIJS, pl: 2 }}>
          {script.pdfNaam} <Box component="span" sx={{ color: GRIJS }}>(script, PDF wordt bij versturen gemaakt)</Box>
        </Typography>
      )}
      {mail && probleem && <Alert severity="error" sx={{ mt: 1 }}>{probleem}</Alert>}
    </Box>
  );
}

function meldResultaat(d, keuze) {
  const delen = [`${(d.ontvangers || []).length} crewleden`];
  const bijlagen = d.bijlagen || [];
  if (keuze.mail) {
    if (d.mail === 'sent') delen.push(bijlagen.length ? `mail verstuurd met ${bijlagen.length} bijlage${bijlagen.length === 1 ? '' : 'n'}` : 'mail verstuurd');
    else if (d.mail === 'dry-run') delen.push(`mail niet echt verstuurd (testomgeving)${bijlagen.length ? `; zou meegaan: ${bijlagen.map((b) => b.naam).join(', ')}` : ''}`);
    else if (d.mail === 'dedupe' && d.mailEerder === 'sent') delen.push('mail was net al verstuurd');
    else delen.push(`mail: ${d.mail}`);
  }
  if ((d.onbekend || []).length) delen.push(`geen e-mail bekend: ${d.onbekend.join(', ')}`);
  const bewezen = d.mail === 'sent' || d.mail === 'dry-run' || (d.mail === 'dedupe' && d.mailEerder === 'sent');
  if (keuze.mail && d.mail === 'dedupe' && !bewezen) {
    // Dezelfde mail is net al geprobeerd maar niet bewezen verstuurd: niet opnieuw, wel eerlijk melden.
    toast.error(`Deze mail is de afgelopen 10 minuten al geprobeerd (${d.mailEerder || 'onbekend'}) en niet opnieuw verstuurd — controleer bij de crew of hij aankwam`, { duration: 12000 });
  } else if (keuze.mail && !bewezen) {
    // 'failed'/'onzeker'/'skipped': nooit als succes tonen — de crew heeft de mail (met bijlagen) misschien niet.
    toast.error(`Mail niet verstuurd (${d.mail}) — ${delen.join(' · ')}`, { duration: 10000 });
  } else {
    toast.success(`Crew geïnformeerd — ${delen.join(' · ')}`, { duration: 8000 });
  }
}

/**
 * Versturen vanuit het venster. Met e-mail: eerst 5 s "Annuleren" (vangnet voor mail),
 * met de bijlagen in de melding. Geeft { ok } terug; bij annuleren/fout ok=false.
 * `vooraf` (optioneel, async): draait vlak vóór het echte verzoek — ook ná de 5 s — en mag gooien
 * om het versturen te stoppen (de checklist controleert zo of de ontvangers nog kloppen).
 * `verwachteOntvangers` (optioneel, e-mailadressen): gaat mee in het verzoek; de server verstuurt
 * alleen als dat precies de ontvangers zijn die hij gebruikt (anders 409, er gaat niets).
 */
export async function informeerCrew({ callsheetId, keuze, overzicht, onStart, vooraf, verwachteOntvangers }) {
  // De bijlagen die de PL nu ziet gaan mee in het verzoek: verandert de keuze tijdens de 5 s (of in
  // een ander tabblad), dan weigert de server in plaats van iets anders te versturen.
  const bevestigd = (overzicht?.gekozen || []).slice();
  // De rev van de scriptkeuze (uniek per keuze): de server weigert als die intussen anders is.
  const bevestigdScript = overzicht?.script?.rev || null;
  const verstuur = async () => {
    if (vooraf) await vooraf();
    const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${callsheetId}/informeer-crew`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        bericht: keuze.bericht, kanalen: { webapp: keuze.webapp, crewApp: keuze.crewApp, mail: keuze.mail },
        ...(keuze.mail ? { bijlagen: bevestigd, script: bevestigdScript } : {}),
        ...(Array.isArray(verwachteOntvangers) ? { verwachteOntvangers } : {}),
      }),
    });
    return leesJson(r, 'Informeren');
  };
  if (!keuze.mail) {
    try {
      const d = await verstuur();
      onStart?.();
      meldResultaat(d, keuze);
      return { ok: true };
    } catch (e) { meldFout(e, 'Informeren'); return { ok: false }; }
  }
  onStart?.();
  const namen = [...gekozenDocumenten(overzicht).map((d) => d.bestandsnaam), ...(overzicht?.script ? [overzicht.script.pdfNaam] : [])];
  const tekst = namen.length
    ? `Mail naar de crew met ${namen.join(', ')} wordt verzonden…`
    : 'Mail naar de crew wordt verzonden…';
  const uit = await voerUitgesteldUit(tekst, verstuur, { duur: 5000 });
  if (uit.geannuleerd || uit.fout) return { ok: false };
  meldResultaat(uit.resultaat, keuze);
  return { ok: true };
}
